import React, { useState, useEffect, useRef } from 'react';
import { formatPrice } from "../utils/formatPrice";
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from "axios";
import HomeNavbar from '../components/HomeNavbar';
import Footer from '../components/Footer';
import SecondaryNavbar from '../components/SecondaryNavbar';
import Advertisement from '../components/Advertisement';
import ProductCarousel from '../components/ProductCarousel';
import ProductsOnSaleCarousel from '../components/ProductsOnSaleCarousel';
import STIHLProductsCarousel from '../components/STIHLProductsCarousel';
import AdditionalCarousel from "../components/AdditionalCarousel";
import CategoryProducts from '../components/CategoryProducts';
import AccountDropdown from '../components/AccountDropdown';
import CartDropdown from '../components/CartDropdown'; // Import CartDropdown
import EweProductCard from "../components/EweProductCard";
import MainCategoriesCarousel from '../components/MainCategoriesCarousel';
import ImageCarousel from '../components/ImageCarousel';
import '../styles/HomePage.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { FaFacebook, FaInstagram, FaSearch, FaBars, FaShoppingCart, FaUser, FaTiktok } from 'react-icons/fa';

const HomePage = ({ cart = [], setCart, isLoggedIn }) => {
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [user, setUser] = useState(null);
  const [guestCartId, setGuestCartId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [productsByCategory, setProductsByCategory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showCartDropdown, setShowCartDropdown] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [isCategoriesDropdownOpen, setIsCategoriesDropdownOpen] = useState(false);
  const [showSaleCarousel, setShowSaleCarousel] = useState(false);
  const productsOnSaleCarouselRef = useRef(null);
  const [carouselActive, setCarouselActive] = useState(false);
  const [carouselCategories, setCarouselCategories] = useState([]);
  const [productsOnSale, setProductsOnSale] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isMobileCategoryOpen, setIsMobileCategoryOpen] = useState(false);
  const [showMobileAccountMenu, setShowMobileAccountMenu] = useState(false);
  const [showMobileSearchBar, setShowMobileSearchBar] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [eweProducts, setEweProducts] = useState([]);
  const [isLoadingEweProducts, setIsLoadingEweProducts] = useState(false);
  const [eweProductsError, setEweProductsError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const searchTimeoutRef = useRef(null);
  const searchAbortControllerRef = useRef(null);
  const [cartUpdateTrigger, setCartUpdateTrigger] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


  const generateGuestCartId = () => 'guest-' + Math.random().toString(36).substr(2, 9);

// Add this useEffect to check auth state on initial load
useEffect(() => {
// Replace your existing loadCartItems function with this fixed version:

const loadCartItems = async () => {
  try {
    // If user is logged in, only load user cart
    if (isLoggedIn && user?.id) {
      console.log('Loading user cart for user:', user.id);
      await loadUserCart(user.id);
      return; // Exit early, don't try to load guest cart
    }

    // Only load guest cart if user is NOT logged in
    if (!isLoggedIn && guestCartId) {
      console.log('Loading guest cart for ID:', guestCartId);
      await loadGuestCart(guestCartId);
      return;
    }

    // If no valid auth and no guest cart ID, create new guest cart
    if (!isLoggedIn && !guestCartId) {
      console.log('Creating new guest cart');
      const newGuestCartId = generateGuestCartId();
      setGuestCartId(newGuestCartId);
      localStorage.setItem("guestCartId", newGuestCartId);
      setCart([]);
    }
  } catch (error) {
    console.error('Error in loadCartItems:', error);
    setCart([]);
  }
};

// Also update your loadInitialCart function in the first useEffect:

const loadInitialCart = async () => {
  try {
    // Check auth status first
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      // User is logged in - verify token is still valid
      try {
        const authResponse = await fetch(`${API_BASE_URL}/auth/check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (authResponse.ok) {
          // Token is valid, use user cart
          const userData = JSON.parse(storedUser);
          setUser(userData);
          // Clear any existing guest cart data
          localStorage.removeItem('guestCartId');
          setGuestCartId(null);
          console.log('Loading cart for authenticated user:', userData.id);
          await loadUserCart(userData.id);
          return; // Exit early - don't create guest cart
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      }

      // Token invalid, clear auth data and fall through to guest
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }

    // No valid auth, use guest cart
    console.log('Setting up guest cart');
    let guestId = localStorage.getItem('guestCartId');
    if (!guestId) {
      guestId = generateGuestCartId();
      localStorage.setItem('guestCartId', guestId);
    }
    setGuestCartId(guestId);
    await loadGuestCart(guestId);

  } catch (error) {
    console.error('Cart initialization error:', error);
    setCart([]);
  }
};

  loadInitialCart();
  fetchCategories();
}, []); // Empty dependency array = runs once on mount// Empty dependency array = runs once on mount

const loadUserCart = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/cart?userId=${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    if (response.ok) {
      const cartData = await response.json();
      setCart(cartData.items || []);
    } else if (response.status === 404) {
      // Cart doesn't exist yet
      setCart([]);
    } else {
      throw new Error(`Failed to load user cart: ${response.status}`);
    }
  } catch (error) {
    console.error('Error loading user cart:', error);
    setCart([]);
  }
};

const loadGuestCart = async (guestId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/cart?guestCartId=${guestId}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 404) {
      // Cart doesn't exist yet
      setCart([]);
      return;
    }

    if (!response.ok) {
      throw new Error(`Failed to load guest cart: ${response.status}`);
    }

    const data = await response.json();
    setCart(data.items || []);
  } catch (error) {
    console.error('Error loading guest cart:', error);
    setCart([]);
  }
};

  // Function to toggle dropdown
  const toggleCategoriesDropdown = () => {
    setIsCategoriesDropdownOpen(!isCategoriesDropdownOpen);
  };


  // Show Mobile Search back button

  useEffect(() => {
    const handleBackButton = () => {
      if (showMobileSearchBar) {
        setShowMobileSearchBar(false);
        setShowSearchDropdown(false);
        document.body.classList.remove('search-active');
      }
    };

    window.addEventListener('popstate', handleBackButton);
    return () => window.removeEventListener('popstate', handleBackButton);
  }, [showMobileSearchBar]);


// In HomePage component, add this useEffect
useEffect(() => {
  if (location.pathname === '/home' && !location.state?.selectedCategory) {
    resetSelectedCategory();
  }
}, [location.pathname, location.state]);
    useEffect(() => {
      if (!location.pathname.startsWith('/home') || location.pathname !== '/home') {
        setIsCategoriesDropdownOpen(false);
      } else {
        setIsCategoriesDropdownOpen(true); // Reopen when returning to home
      }
    }, [location.pathname]);


  const buildCategoryTree = (categories) => {
    const map = {};
    const roots = [];

    categories.forEach((category) => {
      map[category.id] = { ...category, children: [] };
    });

    categories.forEach((category) => {
      if (category.parentId) {
        const parent = map[category.parentId];
        if (parent) parent.children.push(map[category.id]);
      } else {
        roots.push(map[category.id]);
      }
    });

    return roots;
  };

  const getCategoryName = (categoryId) => {
    const findCategory = (categories, targetId) => {
      for (const category of categories) {
        if (category.id === targetId) return category.name;
        if (category.children && category.children.length > 0) {
          const childResult = findCategory(category.children, targetId);
          if (childResult) return childResult;
        }
      }
      return null;
    };

    return categoryId ? findCategory(categories, categoryId) || 'Selected Category' : 'Category';
  };

const fetchProductsByCategory = async (category, page = 0, pageSize = 1000) => {  // Increased pageSize
  if (!category?.id) return { products: [], hasMore: false };

  try {
    let endpoint;
    let params = {};

    if (category.type === 'main') {
      endpoint = `/ewe/products/maincategory/${category.id}`;
      params = { page, size: pageSize };
    } else {
      endpoint = `/ewe/products/${category.type === 'category' ? 'category' : 'subcategory'}/${category.id}`;
    }

    const url = new URL(`${API_BASE_URL}${endpoint}`);
    Object.entries(params).forEach(([key, value]) =>
      url.searchParams.append(key, value));

    console.log("Fetching from:", url.toString());
    const response = await fetch(url.toString());

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch products');
    }

    const data = await response.json();

    // Handle both array and paginated responses
    if (Array.isArray(data)) {
      return {
        products: data,
        hasMore: false,
        totalProducts: data.length
      };
    } else {
      return {
        products: data.content || [],
        hasMore: !data.last,
        totalProducts: data.totalElements || 0
      };
    }
  } catch (error) {
    console.error(`Error fetching ${category.type} products:`, error);
    return { products: [], hasMore: false, totalProducts: 0 };
  }
};

  useEffect(() => {
    if (!showCartDropdown) {
      document.body.style.overflow = '';
    }
  }, [showCartDropdown]);

const resetSelectedCategory = () => {
  setSelectedCategory(null);
  setProductsByCategory([]); // Clear the products for the selected category
};

useEffect(() => {
  // Check if a category ID is passed in the state
  if (location.state?.selectedCategory) {
    setSelectedCategory(location.state.selectedCategory);
    // Load first page of products
    fetchProductsByCategory(location.state.selectedCategory, 0, 200)
      .then(({ products }) => setProductsByCategory(products));
  }
}, [location.state]);


  useEffect(() => {
    const fetchCarouselData = async () => {
      try {
        const [settingsRes, salesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/products/settings/sale-carousel`),
          fetch(`${API_BASE_URL}/products/on-sale`)
        ]);

        if (!settingsRes.ok || !salesRes.ok) throw new Error('Failed to fetch data');

        const isActive = await settingsRes.json();
        const saleProducts = await salesRes.json();

        setCarouselActive(isActive);
        setProductsOnSale(isActive ? saleProducts : []);

      } catch (error) {
        console.error('Error:', error);
        setCarouselActive(true);
        setProductsOnSale([]);
      }
    };

    fetchCarouselData();
  }, [location]);

  useEffect(() => {
    const fetchCarouselCategories = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/products/settings/carousel-categories`
        );
        setCarouselCategories(response.data);
      } catch (error) {
        console.error("Error fetching carousel categories:", error);
      }
    };

    fetchCarouselCategories();
  }, []);



useEffect(() => {
  const fetchUserData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/user`, {
        credentials: 'include' // Important for cookies
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        setUser(null);
        localStorage.removeItem('user');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  if (isLoggedIn) {
    fetchUserData();
  }
}, [isLoggedIn]);


useEffect(() => {
  const userData = localStorage.getItem("user");
  const token = localStorage.getItem("token");

  if (userData && token) {
    try {
      const parsedUserData = JSON.parse(userData);
      setUser(parsedUserData);
      // We'll load the cart after the user is set
    } catch (error) {
      console.error("Error parsing user data:", error);
      setupGuestCart();
    }
  } else {
    setupGuestCart();
  }

  fetchCategories();
}, []);



  const setupGuestCart = () => {
    let savedGuestCartId = localStorage.getItem("guestCartId");
    if (!savedGuestCartId) {
      savedGuestCartId = generateGuestCartId();
      localStorage.setItem("guestCartId", savedGuestCartId);
    }
    setGuestCartId(savedGuestCartId);
  };
  useEffect(() => {
    // Only attempt to load cart items when either user is set or guestCartId is set
    if ((isLoggedIn && user) || (!isLoggedIn && guestCartId)) {
      loadCartItems();
    }
  }, [isLoggedIn, user, guestCartId]);



const fetchCategories = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/ewe/products/categories`);

    const rawCategories = await response.json();

    // Function to recursively process categories and add type information
    const processCategories = (categories, parentId = null) => {
      return categories.map(category => {
        // Determine category type based on parentId and children
        let type;
        if (parentId === null) {
          type = 'main';
        } else if (category.children && category.children.length > 0) {
          type = 'category';
        } else {
          type = 'subcategory';
        }

        return {
          ...category,
          type,
          children: category.children ? processCategories(category.children, category.id) : []
        };
      });
    };

    const processedCategories = processCategories(rawCategories);
    setCategories(processedCategories);
  } catch (error) {
    console.error('Error fetching categories:', error);
    setCategories([]);
  }
};


const handleUpdateQuantity = async (productId, newQuantity) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/cart/update-quantity`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        productId,
        newQuantity,
        userId: user?.id || null,
        guestCartId: !user?.id ? guestCartId : null
      })
    });

    if (!response.ok) throw new Error('Failed to update quantity');
    await loadCartItems();
  } catch (error) {
    console.error('Error updating quantity:', error);
    await loadCartItems();
  }
};
const updateCartInBackend = async (cartItems) => {
  try {
    const response = await fetch(`${API_BASE_URL}/cart/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ cartItems }),
    });

    if (!response.ok) {
      throw new Error('Failed to update cart in backend');
    }

    const updatedCart = await response.json();
    return updatedCart;
  } catch (error) {
    console.error('Error updating cart in backend:', error);
    throw error;
  }
};


const loadCartItems = async () => {
  try {
    // If user is logged in, only load user cart
    if (isLoggedIn && user?.id) {
      console.log('Loading user cart for user:', user.id);
      await loadUserCart(user.id);
      return; // Exit early, don't try to load guest cart
    }

    // Only load guest cart if user is NOT logged in
    if (!isLoggedIn && guestCartId) {
      console.log('Loading guest cart for ID:', guestCartId);
      await loadGuestCart(guestCartId);
      return;
    }

    // If no valid auth and no guest cart ID, create new guest cart
    if (!isLoggedIn && !guestCartId) {
      console.log('Creating new guest cart');
      const newGuestCartId = generateGuestCartId();
      setGuestCartId(newGuestCartId);
      localStorage.setItem("guestCartId", newGuestCartId);
      setCart([]);
    }
  } catch (error) {
    console.error('Error in loadCartItems:', error);
    setCart([]);
  }
};


useEffect(() => {
  if (location.state?.selectedCategory) {
    setSelectedCategory(location.state.selectedCategory);
  }
}, [location.state]);



const handleCategoryClick = async (category) => {
  if (!category?.id || !category?.type) {
    console.error('Invalid category object:', category);
    return;
  }

  try {
    setSelectedCategory(category);
    setProductsByCategory([]); // Clear previous products

    // Load all products at once with large page size
    const { products } = await fetchProductsByCategory(category, 0, 1000);
    setProductsByCategory(products);

    navigate(`/home/category/${category.id}/${category.name.toLowerCase().replace(/\s+/g, '-')}`, {
      state: {
        selectedCategory: category,
        categoryName: category.name,
        categoryProducts: products
      },
      replace: true // This ensures the navigation replaces the current entry
    });

    if (isMobile) {
      setIsMobileCategoryOpen(false);
    }
  } catch (error) {
    console.error('Error fetching products:', error);
    setProductsByCategory([]);
    alert(`Error loading products: ${error.message}`);
  }
};


const handleSearch = async (immediate = false) => {
  const query = searchQuery.trim();

  // Clear previous requests
  if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  if (searchAbortControllerRef.current) {
    searchAbortControllerRef.current.abort();
  }

  if (!query) {
    setSearchResults([]);
    setShowSearchDropdown(false);
    return;
  }

  // Create new abort controller
  searchAbortControllerRef.current = new AbortController();

  // For Enter key: execute immediately
  if (immediate) {
    executeSearch(query);
    return;
  }

  // For typing: debounce 300ms
  searchTimeoutRef.current = setTimeout(() => {
    executeSearch(query);
  }, 300);
};
// Input handlers
const handleInputChange = (e) => {
  setSearchQuery(e.target.value);
  handleSearch(); // Trigger debounced search
};

useEffect(() => {
  return () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }
  };
}, []);

const executeSearch = async (query) => {
  try {
    setShowSearchDropdown(true);

    const response = await fetch(
      `${API_BASE_URL}/ewe/products/search?q=${encodeURIComponent(query)}`,
      {
        signal: searchAbortControllerRef.current?.signal,
        headers: {
          'Content-Type': 'application/json',
          // Add authorization if needed
          ...(isLoggedIn && {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          })
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Search failed');
    }

    const data = await response.json();
    setSearchResults(data);

  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Search error:', error);
      setSearchResults([]);
      // Optional: Show error to user
      alert(`Search failed: ${error.message}`);
    }
  }
};


  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

const handleLoginSuccess = async (userData) => {
  try {
    console.log('Login successful, transitioning to user cart');

    // Clear guest cart from localStorage immediately
    localStorage.removeItem("guestCartId");
    setGuestCartId(null);

    // Set user data
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));

    // Load user cart (this will replace any existing cart items)
    await loadUserCart(userData.id);

    // Force refresh of cart display
    setCartUpdateTrigger(prev => prev + 1);

    console.log('Successfully transitioned to user cart');
  } catch (error) {
    console.error('Error during login cart transition:', error);
  }
};

const handleLogout = async () => {
  try {
    // Clear frontend state first
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);

    // Clear all possible cookie variations
    const domains = [
      window.location.hostname,
      'www.myzmaj.net', // if using subdomains
      'https://www.myzmaj.net' // for development
    ];

    domains.forEach(domain => {
      document.cookie = `token=; Domain=${domain}; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      document.cookie = `token=; Domain=${domain}; Path=/api; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    });

    // Make API call to logout
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });

    // Force hard redirect with cache busting
    window.location.assign(`/home?logout=${Date.now()}`);
  } catch (error) {
    console.error('Logout failed:', error);
    window.location.assign('/home');
  }
};

  const handleRemoveItem = async (productId) => {
    try {
      const token = isLoggedIn ? localStorage.getItem("token") : null;
      const url = new URL(`${API_BASE_URL}/cart/remove`);

      // Use productId instead of itemId
      url.searchParams.append('productId', productId);

      if (isLoggedIn && user?.id) {
        url.searchParams.append('userId', user.id);
      } else if (guestCartId) {
        url.searchParams.append('guestCartId', guestCartId);
      }

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to remove item");
      }

      // Refresh cart after successful removal
      await loadCartItems();
    } catch (error) {
      console.error("Error removing item:", error);
      throw error;
    }
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2);
  };

  const toggleCartDropdown = () => {
    // Force mobile check here
    const isMobile = window.innerWidth <= 768;

    // Close other dropdowns first
    setShowSearchDropdown(false);
    setIsMobileCategoryOpen(false);
    setShowMobileAccountMenu(false);

    // Toggle cart dropdown
    setShowCartDropdown(!showCartDropdown);

    // Special handling for mobile
    if (isMobile) {
      // Log for debugging
      console.log('Mobile cart toggle clicked, new state:', !showCartDropdown);

      if (!showCartDropdown) {
        // Adding cart-open class to body for CSS targeting
        document.body.classList.add('cart-open');
        document.body.style.overflow = 'hidden';
        window.scrollTo(0, 0);
      } else {
        document.body.classList.remove('cart-open');
        document.body.style.overflow = '';
      }
    }
  };

  const toggleMobileCategory = () => {
    setIsMobileCategoryOpen(!isMobileCategoryOpen);
    setShowCartDropdown(false);
    setShowSearchDropdown(false);
    setShowMobileAccountMenu(false);
  };

  const toggleMobileAccountMenu = () => {
    setShowMobileAccountMenu(!showMobileAccountMenu);
    setShowCartDropdown(false);
    setShowSearchDropdown(false);
    setIsMobileCategoryOpen(false);
  };

  const toggleMobileSearch = () => {
    setShowSearchDropdown(!showSearchDropdown);
    setShowCartDropdown(false);
    setIsMobileCategoryOpen(false);
    setShowMobileAccountMenu(false);
  };

// Inside your HomePage component, update the toggleMobileSearchBar function

const toggleMobileSearchBar = () => {
  const newState = !showMobileSearchBar;
  setShowMobileSearchBar(newState);

  if (newState) {
    window.history.pushState({ searchOpen: true }, '');
    document.body.classList.add('search-active');
    setTimeout(() => {
      const searchInput = document.querySelector('.mobile-search-bar input');
      if (searchInput) searchInput.focus();
    }, 50);
  } else {
    if (window.history.state?.searchOpen) {
      window.history.back();
    }
    document.body.classList.remove('search-active');
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchDropdown(false);
  }
};

const handleGlobalAddToCart = () => {
  // Any global actions you want to perform when adding to cart
  console.log('Item added to cart');
  // You might want to trigger a cart refresh here
  loadCartItems();
};
// Fix the closeDropdowns function
const closeDropdowns = (e) => {
  // Cart dropdown
  if (!e.target.closest('.cart-icon') && !e.target.closest('.cart-dropdown-container')) {
    setShowCartDropdown(false);
  }

  // Search dropdown - handle mobile specifically
  const isMobileSearch = isMobile && (
    e.target.closest('.mobile-search-bar') ||
    e.target.closest('.mobile-search-dropdown')
  );

  if (!isMobileSearch && showSearchDropdown) {
    setShowSearchDropdown(false);
  }

  // Categories - Remove the automatic closing logic
  // (Keep this commented out to prevent auto-closing)
  // if (!e.target.closest('.categories-dropdown') &&
  //     !e.target.closest('.categories-button')) {
  //   setIsCategoriesDropdownOpen(false);
  // }

  // Account menu
  if (!e.target.closest('.account-icon') && !e.target.closest('.mobile-account-menu')) {
    setShowMobileAccountMenu(false);
  }
};

  useEffect(() => {
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);

const fetchEweProducts = async () => {
  setIsLoadingEweProducts(true);
  setEweProductsError(null);

  try {
    const response = await fetch(`${API_BASE_URL}/ewe/products/random?limit=20`);
    if (!response.ok) throw new Error('Failed to fetch products');

    const products = await response.json();
    console.log(`Loaded ${products.length} random products`);
    setEweProducts(products);
  } catch (error) {
    setEweProductsError(error.message);
    setEweProducts([]);
  } finally {
    setIsLoadingEweProducts(false);
  }
};
useEffect(() => {
  fetchEweProducts();
}, []);

// In your HomePage component, add this debug code
useEffect(() => {
  if (eweProducts.length > 0) {
    console.log("First product's image data:", eweProducts[0].urlImages);
    console.log("Complete first product data:", eweProducts[0]);
  }
}, [eweProducts]);
  return (
    <div className="app-container">
      <div className="top-navbar">
        <div className="store-address">
          <a
            href="https://www.google.com/maps/search/?api=1&query=Булевар 12. Фебруара 4, Ниш"
            target="_blank"
            rel="noopener noreferrer"
          >
            Bulevar 12.  Februara 4, Niš
          </a>
        </div>
        <div className="social-icon">
          <a href="https://www.facebook.com/myzmaj/?locale=sr_RS" target="_blank" rel="noopener noreferrer">
            <FaFacebook size={24} color="#fff" />
          </a>
        </div>
            <div className="social-icon">
              <a href="https://www.tiktok.com/@myzmaj?is_from_webapp=1&sender_device=pc" target="_blank" rel="noopener noreferrer">
                <FaTiktok size={24} color="#fff" />
              </a>
            </div>
            <div className="social-icon">
              <a href="https://www.instagram.com/myzmajcom?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer">
                <FaInstagram size={24} color="#fff" />
              </a>
            </div>

      </div>

<HomeNavbar
  cart={cart || []}
  setCart={setCart}
  user={user}
  isLoggedIn={isLoggedIn}
  onLoginSuccess={handleLoginSuccess}
  onRemoveItem={handleRemoveItem}
  guestCartId={guestCartId}
  toggleCartDropdown={toggleCartDropdown}
  showCartDropdown={showCartDropdown}
  calculateTotal={calculateTotal}
  setSelectedCategories={setSelectedCategory}
   onUpdateQuantity={handleUpdateQuantity}
  toggleAccountDropdown={() => setShowAccountDropdown(!showAccountDropdown)} // Add this prop
  resetSelectedCategory={resetSelectedCategory}

/>


{showCartDropdown && (
  <CartDropdown
    cartItems={cart}
    onRemoveItem={handleRemoveItem}
    onUpdateQuantity={handleUpdateQuantity}
    isLoggedIn={isLoggedIn}
    user={user}
    onClose={() => setShowCartDropdown(false)} // Add this line
  />
)}

<SecondaryNavbar
  categories={categories}
  handleCategoryClick={handleCategoryClick}
  searchQuery={searchQuery}
  setSearchQuery={setSearchQuery}
  searchResults={searchResults}
  showSearchDropdown={showSearchDropdown}
  setShowSearchDropdown={setShowSearchDropdown}
  handleInputChange={handleInputChange}
  handleKeyPress={handleKeyPress}
  handleSearch={handleSearch}
  isMobileCategoryOpen={isMobileCategoryOpen}
  setIsMobileCategoryOpen={setIsMobileCategoryOpen}
  isCategoriesDropdownOpen={isCategoriesDropdownOpen}
  setIsCategoriesDropdownOpen={setIsCategoriesDropdownOpen}
  navigate={navigate} // Add this
  formatPrice={formatPrice} // Add this
/>

      {/* Mobile Account Menu - Updated to use AccountDropdown component */}
{showMobileAccountMenu && (
  <div className="mobile-account-menu mobile-fullscreen">
    <div className="mobile-dropdown-header">
      <h3>Nalog</h3>
      <button
        className="close-button"
        onClick={() => setShowMobileAccountMenu(false)}
      >
        ✕
      </button>
    </div>
    <div className="mobile-account-content">
      {isLoggedIn ? (
        <div className="mobile-account-dropdown-wrapper">
          {/* Render AccountDropdown without the header */}
          <AccountDropdown handleLogout={handleLogout} user={user} />
        </div>
      ) : (
        <div className="auth-buttons">
          <button
            className="auth-button login"
            onClick={() => {
              navigate('/login');
              setShowMobileAccountMenu(false);
            }}
          >
            Login
          </button>
          <button
            className="auth-button register"
            onClick={() => {
              navigate('/register');
              setShowMobileAccountMenu(false);
            }}
          >
            Register
          </button>
        </div>
      )}
    </div>
  </div>
)}



{/* Mobile Search Dropdown */}
{isMobile && showSearchDropdown && (
  <div
    className="mobile-search-dropdown"
  >
    {searchResults.length > 0 ? (
      searchResults.map((product) => {
        // Calculate sale price if product is on promotion
        const salePrice = product.isPromotion === 1 && product.promoPrice
          ? product.promoPrice
          : product.anPrice;

        const handleProductClick = () => {

          // Close search dropdown and mobile search bar
          setShowSearchDropdown(false);
          setShowMobileSearchBar(false);
          document.body.classList.remove('search-active');

          // Clear search
          setSearchQuery('');
          setSearchResults([]);

          // Navigate to product
          navigate(`/home/product/${product.anProductKey}`, {
            state: { product }
          });
        };

        return (
          <div
            key={product.anProductKey}
            className="search-result-item"
            onClick={handleProductClick}
            onTouchEnd={handleProductClick} // Add touch event
            onMouseDown={(e) => {
              console.log('Mouse down detected');
              e.preventDefault();
            }}
            onTouchStart={(e) => {
              console.log('Touch start detected');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              borderBottom: '1px solid #eee',
              cursor: 'pointer',
              touchAction: 'manipulation',
              userSelect: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              minHeight: '80px',
              backgroundColor: 'white'
            }}
          >
            <div
              style={{
                width: '50px',
                height: '50px',
                marginRight: '12px',
                flexShrink: 0,
                pointerEvents: 'none'
              }}
            >
              <img
                src={
                  product.urlImages?.[0]?.url ||
                  `https://resource.ewe.rs/products/${product.acProduct?.replace(/[^a-zA-Z0-9]/g, '')}.jpg`
                }
                alt={product.acName}
                onError={(e) => {
                  const cleanCode = product.acProduct?.replace(/[^a-zA-Z0-9]/g, '');
                  if (cleanCode) {
                    e.target.src = `https://resource.ewe.rs/products/${cleanCode}_v.jpg`;
                  } else {
                    e.target.onerror = null;
                    e.target.src = "/noImage.jpg";
                  }
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  pointerEvents: 'none'
                }}
              />
            </div>
            <div style={{ flex: 1, pointerEvents: 'none' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                {product.acDept || product.brand}
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#333',
                lineHeight: '1.3',
                marginBottom: '4px'
              }}>
                {product.acName}
              </div>
                        <div className={`search-result-price ${
                          (product.isPromotion === 1 || product.anDiscount > 0) ? 'sale-price' : ''
                        }`}>
                          {formatPrice(product.anRetailPrice)} RSD
                        </div>
            </div>
          </div>
        );
      })
    ) : (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        No products found
      </div>
    )}
  </div>
)}


      {/* Mobile Search Bar */}
{showMobileSearchBar && (
  <div className="mobile-search-bar">
    <input
      type="text"
      placeholder="Pronađi proizvode..."
      className="search-input"
      value={searchQuery}
      onChange={handleInputChange}
      onKeyPress={handleKeyPress}
      ref={(input) => input && input.focus()} // Auto-focus when rendered
    />
    <button
      className="close-search"
      onClick={toggleMobileSearchBar}
    >
      ✕
    </button>
  </div>
)}



 {location.pathname === '/home' && (
<ImageCarousel
  images={[
    { src: '/stihl_akcija.jpg', alt: 'Stihl advertisement' },
    { src: '/dahua_reklama.png', alt: 'Dahua advertisement' },
    { src: '/servis_reklama2.jpg', alt: 'Service advertisement' }
  ]}
/>

    )}
      <div className="home-content">
          <Outlet context={{
                      openCategoriesDropdown: () => setIsCategoriesDropdownOpen(true),
                      closeCategoriesDropdown: () => setIsCategoriesDropdownOpen(false),
            cart,
            setCart,
            isLoggedIn,
            user,
            onLoginSuccess: handleLoginSuccess,
            onRemoveItem: handleRemoveItem,
            guestCartId,
            toggleCartDropdown,
            showCartDropdown,
            calculateTotal,
            resetSelectedCategory,
            productsByCategory,
            selectedCategory,
            handleCategoryClick,
            handleCategoryClick: handleCategoryClick,
            refreshCart: loadCartItems
          }} />

        {!location.pathname.includes('/product/') && (
          <>
{!selectedCategory && (
  <>
    <ProductCarousel
      eweProducts={eweProducts}
      cart={cart}
      setCart={setCart}
      isLoggedIn={isLoggedIn}
      refreshCart={loadCartItems}
    />
    <MainCategoriesCarousel
      isLoggedIn={isLoggedIn}
      refreshCart={loadCartItems}
      handleCategoryClick={handleCategoryClick}
    />
    {carouselCategories.map((categoryId) => (
      <AdditionalCarousel
        key={categoryId}
        categoryId={categoryId}
        categoryName={getCategoryName(categoryId)}
        cart={cart}
        setCart={setCart}
        isLoggedIn={isLoggedIn}
      />
    ))}
  </>
)}
          </>
        )}
      </div>

      <Footer />

      {/* Mobile Bottom Bar - Updated with reordered icons */}
      <div className="mobile-bottom-bar">
        <div className={`icon-container ${showMobileSearchBar ? 'active-icon' : ''}`}  onClick={toggleMobileSearchBar}>
          <FaSearch size={24} />
          <span className="icon-label">Pretraži</span>
        </div>

        <div className="icon-container account-icon" onClick={toggleMobileAccountMenu}>
          <FaUser size={24} />
          <span className="icon-label">Nalog</span>
        </div>

        <div
          className="icon-container cart-icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log("Cart icon clicked in mobile view");
            toggleCartDropdown();
          }}
        >
          <FaShoppingCart size={24} />
          {cart.length > 0 && (
            <span className="cart-count">{cart.length}</span>
          )}
          <span className="icon-label">Korpa</span>
        </div>

        <div className="icon-container category-icon" onClick={toggleMobileCategory}>
          <FaBars size={24} />
          <span className="icon-label">Kategorije</span>
        </div>
      </div>
    </div>
  );
};

export default HomePage;